import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTask } from "./AppTask";
import { AppTaskList } from "./AppTaskList";
import { AppTaskChip, AppTaskRules } from "./AppTaskRules";

// AppTaskUi - the task UI object (owner, 2026-11-02): ONE object draws an `AppTask` onto its element. The page stays
// script-free, so the element is the page's own strip item (`li.tk`, written by the Bridge's `TaskStateHtml`); this
// binds to it and keeps it in step with the task: `data-state`, the tick's pressed look and words, and the due chip.
// The SAME element is the desktop strip and the phone's task sheet (`AppPhoneChatRun` only opens it as a sheet), so
// both views are drawn by this one object. Markup, classes and attributes are unchanged.

/** The element it draws on (the browser's element fits by shape; `AppTaskFrame.ITfNode` too). */
export interface IAppTaskUiNode {
    className: string;
    textContent: string | null;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    querySelector(Selector: string): IAppTaskUiNode | null;
}

export class AppTaskUi {
    public static readonly StateAttr: string = "data-state";
    public static readonly ActAttr: string = "data-wac-tfact";
    public static readonly TickClass: string = "wac-tftick";
    public Task: AppTask;
    public Node: IAppTaskUiNode;
    private List: AppTaskList;

    public constructor(Task: AppTask, Node: IAppTaskUiNode, List: AppTaskList) {
        this.Task = Task;
        this.Node = Node;
        this.List = List;
    }

    /** The task takes `State` (through its list, so the listeners hear it) and the element shows it. */
    public Show = (State: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.List.SetState(this.Task.Id, State, nz(this.Node.getAttribute("data-until")));
            this.Node.setAttribute(AppTaskUi.StateAttr, State);
            AppTaskUi.Paint(this.Node);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskUi.Show");
        }
    };

    /** The tick reads what a press would do: Mark done, or Reopen on a decided task. */
    public static Paint = (Item: IAppTaskUiNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Tick: IAppTaskUiNode | null = Item.querySelector("." + AppTaskUi.TickClass);
            if (Tick === null) { return; }
            const State: string = nz(Item.getAttribute(AppTaskUi.StateAttr));
            const Decided: boolean = State === "done" || State === "dismissed";
            let Act: string = "done";
            let Label: string = "Mark done";
            if (Decided === true) { Act = "reopen"; Label = "Reopen"; }
            Tick.setAttribute(AppTaskUi.ActAttr, Act);
            Tick.setAttribute("aria-pressed", String(Decided));
            Tick.setAttribute("title", Label);
            Tick.setAttribute("aria-label", Label);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskUi.Paint");
        }
    };

    /** The page's neutral due chip, as the owner's day reads it: "3 d late" / "Today" / the date. */
    public static Recolour = (Item: IAppTaskUiNode, Today: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Chip: AppTaskChip = AppTaskRules.StripChip(nz(Item.getAttribute("data-due")), Today);
            const Node: IAppTaskUiNode | null = Item.querySelector(".due");
            if (Node === null || Chip.Text.length === 0) { return; }
            Node.textContent = Chip.Text;
            Node.className = "due u-" + Chip.Cls;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskUi.Recolour");
        }
    };
}
